'use client';

import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { useLocale, useTranslations } from 'next-intl';

import { LanguageMenu } from '@/components/language-menu';
import { Logo } from '@/components/logo';
import { PaymentBadges } from '@/components/payment-badges';
import { Link } from '@/i18n/navigation';
import { apidocsHref } from '@/lib/apidocs-host';
import { COMPANY } from '@/lib/company';
import { openCookiePreferences } from '@/lib/cookie-consent';
import { dur, ease, sceneItem, spring, stagger } from '@/lib/motion';
import { siteLinks } from '@/lib/site-links';
import type { SiteLinkKey } from '@/lib/site-links';

/** Locale'i koruyan Link + Motion. Header'daki ile aynı kalıp. */
const MotionLink = motion.create(Link);

/**
 * Footer — sayfanın sonu.
 *
 * ---------------------------------------------------------------------------
 * Beyaz (kullanıcı kararı, 9 Ağu 2026)
 * ---------------------------------------------------------------------------
 * Footer önceden kapanış CTA'sı ile tek bir koyu gövdenin — `.dark-close` —
 * içinde saydam duruyordu. O kap kaldırıldı: koyu yüzey artık yalnızca CTA'nın
 * şeridi, footer beyaz ve kendi zeminini taşıyor. Sayfanın sonu "son bir
 * vurgu, sonra sessiz bir dizin" oluyor.
 *
 * Mürekkep tonları buna göre yeniden seçildi; hepsi beyaz üstünde en az 4.5:1
 * (globals.css → "Footer").
 *
 * Koyu şerit footer'ın üst kenarına TAM YAPIŞIK (kullanıcı kararı): aradaki
 * boşluğu footer'ın kendi üst dolgusu veriyor, ayrıca bir ayraç çizgisi ya da
 * dış boşluk yok.
 *
 * ---------------------------------------------------------------------------
 * Sütun hattı burada bitiyor
 * ---------------------------------------------------------------------------
 * Sayfanın ızgara dili 1px'lik dikey hatlar; CTA onları yukarıdan aşağı
 * çiziyor ve bilinçli olarak alt kenarı kapatmıyor ("sütun burada kapanmıyor,
 * footer'a devam ediyor" — globals.css → "Kapanış CTA"). Footer o hattı aynı
 * yönde, aynı eğriyle sürdürüp sayfanın son pikselinde bitiriyor. Bu yüzden
 * hatlar burada da `border` değil, çizilen `<motion.span>`.
 *
 * Hat CTA'nın koyu kartının değil, SÜTUNUN devamı: kart hattın içinde yüzüyor,
 * hattı taşımıyor. İkisi de artık açık zemin için siyah %12.
 *
 * ---------------------------------------------------------------------------
 * Ölü bağlantı yok
 * ---------------------------------------------------------------------------
 * Sütunlardaki sayfaların çoğu henüz açılmadı. Adres ve "sayfa var mı"
 * bilgisi `lib/site-links.ts`'te; burada karar verilmiyor, yalnızca
 * uygulanıyor. Yayında olan kayıt gerçek `<a>` basıyor; olmayan kayıt
 * tıklanamayan düz metin oluyor. Sayfa açıldığında tek değişiklik o dosyadaki
 * `live: true` — buraya dokunulmuyor.
 *
 * Durum satır satır DEĞİL, bir kez söyleniyor: sütunların altındaki tek
 * cümle. Denenip elenen sürüm her satıra "YAKINDA" etiketi koyuyordu —
 * on beş kalemin on beşi de açılmamış olduğu için footer bir rozet tarlasına
 * dönüyor, uzun adların yanında etiket alt satıra düşüp liste tırtıklı
 * duruyordu. Cümle, sayfa açıldıkça kendiliğinden küçülen bir bilgi:
 * hiç bekleyen kalmadığında hiç basılmıyor.
 *
 * Cümle görünüşe değil YAPIYA atıf yapıyor ("bağlantısı olmayan başlıklar"),
 * "soluk olanlar" demiyor: rengi görmeyen kullanıcı için de doğru kalsın.
 *
 * Şu an bu yüzden footer'daki tek gerçek bağlantı kelime işareti (anasayfa) ve
 * dil değiştirici. Geçici bir hâl, tasarım hatası değil.
 *
 * ---------------------------------------------------------------------------
 * Metin nereden geliyor
 * ---------------------------------------------------------------------------
 * Ürün ve Kaynaklar sütunlarının adları `header.menu.*` anahtarlarından — yani
 * header'ın mega menüsüyle BİREBİR aynı mesajdan — okunuyor. Kopyalansaydı iki
 * liste zamanla ayrışırdı; aynı sayfanın adı sitenin iki yerinde farklı olmaz.
 * Aynı gerekçeyle "Fiyatlandırma" da `header.pricing`.
 *
 * ---------------------------------------------------------------------------
 * Hareket neyi açıklıyor (DESIGN.md §4)
 * ---------------------------------------------------------------------------
 *   • Hatlar → CTA'dan gelen dikey çizgi inmeye devam ediyor ve burada
 *              duruyor. "Sayfa buraya kadar."
 *   • Giriş  → önce marka, sonra dört sütun sırayla odaklanıyor. Ölçülü
 *              taraftayız: burası bir kapanış, bir sahne değil.
 *   • Künye  → şirket satırı ve ödeme şeridi sütunlardan sonra, ayraçtan önce
 *              aynı sırayla odaklanıyor.
 *   • Ayraç  → alt şeridin üstündeki hat soldan sağa çiziliyor; telif ve dil
 *              satırı ondan sonra geliyor.
 *   • Satır  → yalnızca GERÇEKTEN bir yere giden bağlantı hover'da 2px sağa
 *              kayıyor. Yayında olmayan satır kımıldamıyor — hareketin kendisi
 *              "bu tıklanır" bilgisi (globals.css).
 *   • Dil    → seçilinin zemini `layoutId` ile diğerine taşınıyor, üstelik
 *              tıklama anında: sayfa yüklenirken arayüz ölü durmuyor.
 */

/**
 * Footer'ın iki tür satırı var:
 *
 *   • `site` → sitenin kendi sayfası. Adres ve "yayında mı" bilgisi
 *     `lib/site-links.ts`'te; burada karar verilmiyor.
 *   • `docs` → apidocs.mentorivo.com. AYRI bir alan adı olduğu için site
 *     haritasına girmiyor: o harita dil öneksiz iç adresleri tutuyor ve
 *     `Link` öneki kendisi koyuyor. Doküman ise kendi dil önekini taşıyan
 *     mutlak bir adres, yani düz `<a>` ile basılmak zorunda.
 *
 * Ayrım tip düzeyinde: `route` ile `docs` yan yana opsiyonel alanlar
 * olsaydı ikisi birden dolu (ya da ikisi birden boş) bir satır yazılabilirdi.
 */
type FooterLink =
  | {
      kind: 'site';
      /** `lib/site-links.ts` kaydı — adres ve yayında olup olmadığı orada. */
      route: SiteLinkKey;
      /** Kök namespace'ten tam mesaj anahtarı. */
      label: string;
    }
  | { kind: 'docs'; label: string };

type FooterColumn = {
  id: string;
  /** Sütun başlığının mesaj anahtarı; `<nav>`'ın erişilebilir adı da bu. */
  title: string;
  links: readonly FooterLink[];
};

const COLUMNS: readonly FooterColumn[] = [
  {
    id: 'product',
    title: 'footer.columns.product',
    links: [
      /* Sıra header'ın mega menüsüyle aynı (14 Eki 2026). */
      { kind: 'site', route: 'featureAgent', label: 'header.menu.agent.title' },
      { kind: 'site', route: 'featureAutopilot', label: 'header.menu.autopilot.title' },
      { kind: 'site', route: 'featureCreative', label: 'header.menu.creative.title' },
      { kind: 'site', route: 'featureCampaigns', label: 'header.menu.campaigns.title' },
    ],
  },
  {
    id: 'resources',
    title: 'footer.columns.resources',
    links: [
      { kind: 'site', route: 'blog', label: 'header.menu.blog.title' },
      /* 🔴 Ürün dokümanı satırı (`/docs`) 18 Eyl 2026'da KALDIRILDI: yardım
         merkeziyle tek sayfada birleşti (kullanıcı kararı, gerekçe
         `lib/site-links.ts`te). Önceki sürümde burada "ikisi aynı satır
         değil" diye bir not vardı ve o not ÜRÜN dokümanı ile GELİŞTİRİCİ
         dokümanını ayırıyordu — o ayrım hâlâ doğru ve hâlâ duruyor, aşağıdaki
         `kind: 'docs'` satırı tam olarak o. Kalkan şey ürün dokümanının yardım
         merkezinden ayrı bir satır olmasıydı; ikisi aynı soruya cevap
         veriyordu. */
      { kind: 'docs', label: 'footer.resources.apiDocs' },
      { kind: 'site', route: 'changelog', label: 'header.menu.changelog.title' },
      { kind: 'site', route: 'help', label: 'header.menu.help.title' },
    ],
  },
  {
    id: 'company',
    title: 'footer.columns.company',
    links: [
      { kind: 'site', route: 'about', label: 'footer.company.about' },
      { kind: 'site', route: 'contact', label: 'footer.company.contact' },
      { kind: 'site', route: 'pricing', label: 'header.pricing' },
    ],
  },
  {
    id: 'legal',
    title: 'footer.columns.legal',
    links: [
      { kind: 'site', route: 'privacy', label: 'footer.legal.privacy' },
      { kind: 'site', route: 'terms', label: 'footer.legal.terms' },
      { kind: 'site', route: 'distanceSales', label: 'footer.legal.distanceSales' },
      { kind: 'site', route: 'preInformation', label: 'footer.legal.preInformation' },
      /* 14 Eki 2026 — iyzico incelemesinin istediği sayfa. Satış belgelerinin
         yanında: üçü birlikte "satın alırsam ne olur" sorusunu cevaplıyor. */
      { kind: 'site', route: 'deliveryReturns', label: 'footer.legal.deliveryReturns' },
      { kind: 'site', route: 'dataProtection', label: 'footer.legal.dataProtection' },
      { kind: 'site', route: 'cookies', label: 'footer.legal.cookies' },
    ],
  },
];

/* Önce hatlar, sonra içerik — cta.tsx ile aynı kurulum sırası. */
const frameV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

/**
 * Dikey hat yukarıdan aşağı iniyor. Değerler cta.tsx'teki `ruleV` ile BİREBİR
 * aynı olmak zorunda: bu iki çizgi görsel olarak tek bir hattın devamı, farklı
 * süre ya da eğri kullanılırsa sınırda hız değiştiren tek bir çizgi görünür.
 * Yay değil tween — çizilen hattın fiziksel karşılığı yok, spring'in aşımı ucu
 * hedefin altına taşırıp geri çekerdi.
 */
const ruleV: Variants = {
  hidden: { scaleY: 0, opacity: 0 },
  show: {
    scaleY: 1,
    opacity: 1,
    transition: {
      duration: dur.slow,
      ease: ease.out,
      opacity: { duration: dur.fast, ease: ease.out },
    },
  },
};

const contentV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base, delayChildren: 0.06 } },
};

/** Alt şeridin ayracı — dikeylerin aksine SOLDAN SAĞA. */
const dividerV: Variants = {
  hidden: { scaleX: 0, opacity: 0 },
  show: {
    scaleX: 1,
    opacity: 1,
    transition: {
      duration: dur.slow,
      ease: ease.out,
      opacity: { duration: dur.fast, ease: ease.out },
    },
  },
};

export function SiteFooter({ year }: { year: number }) {
  /* Kök namespace: sütun adları hem `footer.*` hem `header.*` altından
     geliyor (yukarıdaki nota bak), tek namespace yetmiyor. */
  const t = useTranslations();
  /* Doküman satırı için: apidocs kendi dil önekini yoldan okuyor. */
  const locale = useLocale();

  return (
    <footer className="site-footer">
      {/* Sütun hero, CTA ve SSS ile aynı: .grid-item-8fr kendi `grid-column: 2`
          kuralıyla yerleşiyor, boş kardeşe gerek yok. */}
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <motion.div
            className="site-footer-frame"
            variants={frameV}
            initial={false}
            animate="show"
            /* amount düşük: footer uzun, 390px'te ekranın tamamından büyük.
               0.3 istenseydi kullanıcı sayfanın dibine varmadan hiçbir şey
               görünmezdi. */
          >
            <motion.span
              className="site-footer-rule site-footer-rule--l"
              variants={ruleV}
              aria-hidden="true"
            />
            <motion.span
              className="site-footer-rule site-footer-rule--r"
              variants={ruleV}
              aria-hidden="true"
            />

            <motion.div className="site-footer-content" variants={contentV}>
              <div className="site-footer-top">
                <motion.div className="site-footer-brand" variants={sceneItem}>
                  {/* Header'daki kilidin AYNISI (.header-logo) — footer'a
                      ikinci bir logo dili icat etmek markayı iki başlı
                      gösterir. Tek fark mürekkep rengi: kelime işareti
                      `currentColor`, açık zeminde koyuya dönüyor (CSS'te). */}
                  <MotionLink
                    href={siteLinks.home.href}
                    className="site-footer-logo-link"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    transition={spring.snap}
                  >
                    <Logo className="header-logo" />
                  </MotionLink>
                  <p className="site-footer-tagline">{t('footer.tagline')}</p>
                </motion.div>

                {/* Her sütun kendi `<nav>` landmark'ı: ekran okuyucu footer'ı
                    tek bir bağlantı yığını olarak değil, adlandırılmış dört
                    bölüm olarak geziyor. Arama motoru site yapısını da buradan
                    çıkarıyor — bu yüzden yayındaki her hedef gerçek `<a>`. */}
                <div className="site-footer-navs">
                  <div className="site-footer-cols">
                    {COLUMNS.map((col) => (
                      <motion.nav
                        key={col.id}
                        className="site-footer-col"
                        aria-label={t(col.title)}
                        variants={sceneItem}
                      >
                        <h2 className="site-footer-col-title">{t(col.title)}</h2>
                        <ul className="site-footer-list">
                          {col.links.map((link) => {
                            /* Doküman: mutlak adres, dil önekini `apidocsHref`
                               koyuyor. `Link` değil düz `<a>` — next-intl
                               mutlak adrese önek eklemez, ekleseydi de yanlış
                               olurdu. Hover kayması sınıftan geliyor
                               (globals.css → `a.site-footer-link:hover`), yani
                               satır komşularıyla aynı davranıyor. */
                            if (link.kind === 'docs') {
                              return (
                                <li key="apiDocs">
                                  <a href={apidocsHref(locale)} className="site-footer-link">
                                    {t(link.label)}
                                  </a>
                                </li>
                              );
                            }

                            const target = siteLinks[link.route];

                            return (
                              <li key={link.route}>
                                {target.live ? (
                                  <Link href={target.href} className="site-footer-link">
                                    {t(link.label)}
                                  </Link>
                                ) : (
                                  /* Tıklanabilir DEĞİL: ne `<a>`, ne `href`,
                                     ne imleç — tıklanacak bir sayfa yok
                                     (lib/site-links.ts). */
                                  <span className="site-footer-link site-footer-link--soon">
                                    {t(link.label)}
                                  </span>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </motion.nav>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── Künye + ödeme şeridi (14 Eki 2026) ──────────────────────
                  iyzico'nun üye iş yeri incelemesi iki şey istiyor: satıcının
                  kim olduğu sitede açıkça yazsın ve "iyzico ile Öde" + kart
                  logoları görünsün. İkisi de HER sayfada lazım (inceleyen
                  kişi hangi sayfadan girerse girsin), yeri bu yüzden footer.

                  Unvan/adres/vergi no çevrilmiyor — `lib/company.ts`ten.
                  `<address>` çünkü sayfanın sahibinin iletişim bilgisi;
                  tarayıcının italik varsayılanı CSS'te kapatılıyor.
                  E-posta ve telefon burada yok: dokunmatik hedefi 44px olan
                  iki bağlantı bu sessiz satırı ikinci bir menüye çevirirdi;
                  ikisi de bir tık ötede, /contact künyesinde. */}
              <motion.div className="site-footer-trust" variants={sceneItem}>
                <address className="site-footer-entity">
                  <span className="site-footer-entity-brand">
                    {t('footer.entity.brand', { company: COMPANY.legalName })}
                  </span>
                  <span>{COMPANY.address}</span>
                  <span>
                    {t('footer.entity.tax', {
                      number: COMPANY.taxNumber,
                      office: COMPANY.taxOffice,
                    })}
                  </span>
                </address>
                <PaymentBadges />
              </motion.div>

              <motion.span className="site-footer-divider" variants={dividerV} aria-hidden="true" />

              <motion.div className="site-footer-bottom" variants={sceneItem}>
                <div className="site-footer-bottom-left">
                  <p className="site-footer-copy">
                    {t('footer.copyright', { year: String(year) })}
                  </p>
                  {/* Kararı geri almanın TEK yolu bu giriş. Şerit bir kez cevap
                      alınca bir daha kendiliğinden çıkmıyor; onayın geri
                      alınabilir olması KVKK/GDPR'ın şartı, yani bu düğme
                      süs değil şeridin ikinci yarısı. Yeri footer: çerez
                      politikası bağlantısının hemen komşusu. */}
                  <button
                    type="button"
                    className="site-footer-cookie-btn"
                    onClick={openCookiePreferences}
                  >
                    {t('footer.cookiePrefs')}
                  </button>
                </div>
                {/* 13 dil (29 Eyl 2026): iki dillik bölümlü düğme yerine açılır
                    menü — gerekçe `components/language-menu.tsx` başında.
                    Footer sayfanın dibinde, panel YUKARI açılıyor. */}
                <LanguageMenu placement="top" />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </footer>
  );
}
